import React, { useEffect, useState, useRef } from 'react';
import { ProfileEditFormFields } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, ButtonsWrapper, GroupInputs, StyledCheckbox, InfoWrapper } from './profile-edit.styles';
import { Typography } from '../../../../../shared/ui/typography/typography';
import { Field, Form, FormSpy, FormRenderProps } from 'react-final-form';
import { validateEditProfile } from '../../lib/utils/form-edit-profile-validate/form-edit-profile-validate';
import { ProfileEditFormKeys } from '../../config/form-constants';
import { useProfileEditMutation } from '../../api/profile-edit-api';
import { CACHE_KEY_PROFILE_EDIT } from '../../config/api-constants';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { InputPhone } from '@shared/ui/input-phone/input-phone';
import { InputText } from '@shared/ui/input-text/input-text';
import { Toast } from '@shared/ui/toast/toast';
import toast from 'react-hot-toast';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useLazyCurrentUserQuery } from '@entities/current-user/api/credentials-user-api';
import { ModalCancelProfileEdit } from './modal-cancel-profile-edit/modal-cancel-profile-edit';
import { sortObject } from './sort-object';
import { TEXT_MASK_PATTERN } from '@shared/ui/input-text/constants';
import { Select } from '@shared/ui/select/select';
import { DEFAULT_USER_PROFILE_OPTIONS_LIST_PARAMETERS } from '@entities/user-profile-option/config/store-constants';
import { UserProfileOptionDataRequest } from '@entities/user-profile-option/api/user-profile-option-types';
import { useUserProfileOptionsInfinityListQuery } from '@entities/user-profile-option/api/user-profile-option-api';
import { useDepartmentSelect } from '@entities/user-profile-option/libs/hooks/use-department-options/use-department-options';
import { useNormalizeInitialValues } from '../../lib/hooks/use-normalize-initial-values/use-normalize-initial-values';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';
import { ROUTE_ADMIN_MAIN } from '@shared/config/routes/routes';

export const ProfileEdit: React.FC = () => {
    const redirect = useRedirect();
    const user = useCurrentUserSelector();
    const [isSendingForm, setIsSendingForm] = useState(false);
    const [editProfile, editProfileResult] = useProfileEditMutation({ fixedCacheKey: CACHE_KEY_PROFILE_EDIT });
    const [getCurrentUser] = useLazyCurrentUserQuery();
    const [isShowModal, setIsShowModal] = useState(false);
    const [isStateChanged, setIsStateChanged] = useState(false);
    const formRef = useRef<FormRenderProps<ProfileEditFormFields, Partial<ProfileEditFormFields>> | null>(null);
    const initialValues = useNormalizeInitialValues();

    /* Department */
    const [departmentListParameters, setDepartmentListParameters] = useState<UserProfileOptionDataRequest>({
        ...DEFAULT_USER_PROFILE_OPTIONS_LIST_PARAMETERS,
        take: 10,
    });
    const {
        data: departmentsList,
        isLoading: isDepartmentsListLoading,
        isFetching: isDepartmentsListFetching,
    } = useUserProfileOptionsInfinityListQuery(departmentListParameters);

    const {
        departmentOptions,
        departmentEmptyOption,
        isShowEmptyLoaderDepartment,
        infinityScrollDepartmentProps,
        onSearchDepartment,
    } = useDepartmentSelect({
        departmentsData: departmentsList,
        isLoading: isDepartmentsListLoading,
        isFetching: isDepartmentsListFetching,
        setDepartmentListParameters,
    });

    const handleRedirect = () => {
        redirect({ url: `${ROUTE_ADMIN_MAIN}` });
    };

    const handleDecline = () => {
        if (isStateChanged) {
            return setIsShowModal(true);
        }
        handleRedirect();
    };

    const handleSubmit = async (fields: ProfileEditFormFields) => {
        if (!user) {
            return;
        }
        const departmentOption = fields[ProfileEditFormKeys.Department][0];
        setIsSendingForm(true);
        await editProfile({
            id: user.id,
            firstName: fields[ProfileEditFormKeys.Name] || '',
            lastName: fields[ProfileEditFormKeys.Surname] || '',
            middleName: fields[ProfileEditFormKeys.Patronymic] || '',
            phoneNumber: fields[ProfileEditFormKeys.Phone],
            departmentId: departmentOption ? Number(departmentOption.value) : 0,
            jobTitle: fields[ProfileEditFormKeys.JobTitle],
            receivingPromotionalMaterials: fields[ProfileEditFormKeys.AgreementAdvert],
        });
    };

    useEffect(() => {
        if (editProfileResult.isSuccess) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='success'
                        title='Успешно!'
                        description={'Профиль успешно изменен'}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            editProfileResult.reset();
        } else if (editProfileResult.isError) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`Не удалось изменить профиль.\n${getQueryError(editProfileResult)?.message}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            editProfileResult.reset();
        }
    }, [editProfileResult.isSuccess, editProfileResult.isError]);

    useEffect(() => {
        (async function () {
            if (editProfileResult.isSuccess) {
                await getCurrentUser(undefined, false);
                formRef.current?.form.reset();
                setIsSendingForm(false);
            }
        })();
    }, [editProfileResult.isSuccess]);

    const handleClose = () => {
        setIsShowModal(false);
    };

    return (
        <>
            <Form
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validate={validateEditProfile}
                render={(form) => {
                    formRef.current = form;
                    return (
                        <>
                            <Body>
                                <InfoWrapper>
                                    <Typography size='h6'>Основные данные</Typography>
                                    <GroupInputs>
                                        <Field
                                            name={ProfileEditFormKeys.Surname}
                                            render={({ input, meta }) => (
                                                <InputText
                                                    required
                                                    label='Фамилия'
                                                    placeholder='Введите фамилию'
                                                    name={ProfileEditFormKeys.Surname}
                                                    value={input.value}
                                                    error={meta.touched && meta.error}
                                                    disabled={isSendingForm}
                                                    onChange={input.onChange}
                                                    mask={TEXT_MASK_PATTERN}
                                                />
                                            )}
                                        />
                                        <Field
                                            name={ProfileEditFormKeys.Name}
                                            render={({ input, meta }) => {
                                                return (
                                                    <InputText
                                                        required
                                                        label='Имя'
                                                        placeholder='Введите имя'
                                                        name={ProfileEditFormKeys.Name}
                                                        value={input.value}
                                                        error={meta.touched && meta.error}
                                                        disabled={isSendingForm}
                                                        onChange={input.onChange}
                                                        mask={TEXT_MASK_PATTERN}
                                                    />
                                                );
                                            }}
                                        />
                                        <Field
                                            name={ProfileEditFormKeys.Patronymic}
                                            render={({ input, meta }) => {
                                                return (
                                                    <InputText
                                                        label='Отчество'
                                                        placeholder='Введите отчество'
                                                        name={ProfileEditFormKeys.Name}
                                                        value={input.value}
                                                        error={meta.touched && meta.error}
                                                        disabled={isSendingForm}
                                                        onChange={input.onChange}
                                                        mask={TEXT_MASK_PATTERN}
                                                    />
                                                );
                                            }}
                                        />
                                    </GroupInputs>
                                </InfoWrapper>

                                <InfoWrapper>
                                    <Typography size='h6'>Дополнительные данные</Typography>
                                    <Field
                                        name={ProfileEditFormKeys.JobTitle}
                                        render={({ input, meta }) => (
                                            <InputText
                                                {...input}
                                                required
                                                label='Должность'
                                                placeholder='Введите должность'
                                                name={ProfileEditFormKeys.JobTitle}
                                                error={meta.touched && meta.error}
                                                disabled={isSendingForm}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={ProfileEditFormKeys.Department}
                                        render={({ input, meta }) => {
                                            return (
                                                <Select
                                                    required
                                                    isAsync
                                                    clearable
                                                    label='Выберите департамент'
                                                    placeholder='Выберите департамент из списка'
                                                    disabled={isSendingForm}
                                                    isShowPreloaderInList={isShowEmptyLoaderDepartment}
                                                    onSearch={onSearchDepartment}
                                                    infinityScrollProps={infinityScrollDepartmentProps}
                                                    values={input.value || departmentEmptyOption}
                                                    options={departmentOptions}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        defaultValue={'+7'}
                                        name={ProfileEditFormKeys.Phone}
                                        render={({ input, meta }) => (
                                            <InputPhone
                                                {...input}
                                                required
                                                label='Номер телефона'
                                                name={ProfileEditFormKeys.Phone}
                                                error={meta.touched && meta.error}
                                                disabled={isSendingForm}
                                            />
                                        )}
                                    />
                                    <Field
                                        type='checkbox'
                                        name={ProfileEditFormKeys.AgreementAdvert}
                                        render={({ input }) => (
                                            <StyledCheckbox
                                                label='Согласие на получение рекламных материалов'
                                                name={ProfileEditFormKeys.AgreementAdvert}
                                                checked={input.checked}
                                                disabled={isSendingForm}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                </InfoWrapper>
                            </Body>
                            <FormSpy
                                subscription={{ initialValues: true, values: true }}
                                onChange={({ initialValues, values }) => {
                                    setIsStateChanged(
                                        JSON.stringify(sortObject(initialValues)) !== JSON.stringify(sortObject(values))
                                    );
                                }}
                            />
                            <ButtonsWrapper>
                                <Button
                                    buttonType='primary'
                                    preloader={isSendingForm}
                                    disabled={isSendingForm || !isStateChanged}
                                    onClick={form.handleSubmit}>
                                    Сохранить
                                </Button>
                                <Button disabled={isSendingForm} buttonType='light' onClick={handleDecline}>
                                    Отменить
                                </Button>
                            </ButtonsWrapper>
                        </>
                    );
                }}
            />
            <ModalCancelProfileEdit isShow={isShowModal} onAccept={handleRedirect} onClose={handleClose} />
        </>
    );
};
